import { useCallback, useState } from 'react';
import { saveAs } from 'file-saver';
import JSZip from 'jszip';
import { useDownloadUrlsMutation } from '../../../api';
import { fetchFilesFromUrls, getMimeTypeFromFilename, isMobilePlatform } from '../utils/downloadHelpers';

/**
 * Custom hook for handling photo download functionality
 * @param {Set} selectedPhotos - Set of selected photo filenames
 * @param {function} onDownloadComplete - Callback when download is complete
 * @param {function} onDownloadStart - Callback when download starts
 * @param {function} onDownloadEnd - Callback when download ends (success or error)
 * @param {function} onDownloadProgress - Callback for progress updates (current, total, stage)
 * @returns {Object} Download functions and state
 */
export const usePhotoDownload = (selectedPhotos, onDownloadComplete, onDownloadStart, onDownloadEnd, onDownloadProgress) => {
  const downloadUrlsMutation = useDownloadUrlsMutation();
  const [downloadState, setDownloadState] = useState({
    isDownloading: false,
    current: 0,
    total: 0,
    stage: 'idle' // 'idle' | 'preparing' | 'downloading' | 'packaging' | 'complete'
  });

  const downloadSelectedPhotos = useCallback(async () => {
    if (!selectedPhotos || selectedPhotos.size === 0) return;

    const totalFiles = selectedPhotos.size;
    setDownloadState({ isDownloading: true, current: 0, total: totalFiles, stage: 'preparing' });

    try {
      if (onDownloadStart) onDownloadStart();
      
      // Request presigned URLs for all selected files
      if (onDownloadProgress) onDownloadProgress(0, totalFiles, 'preparing');
      const response = await downloadUrlsMutation.mutateAsync({
        fileNames: Array.from(selectedPhotos)
      });
      
      const { urls } = response;
      if (!urls || !Array.isArray(urls)) {
        console.error('Invalid response structure:', response);
        alert('Failed to get download URLs. Please try again.');
        return;
      }

      // Check for errors in URL generation
      const errors = urls.filter(({ error }) => error);
      const validUrls = urls.filter(({ url, error }) => url && !error);
      
      if (errors.length > 0) {
        console.warn('Some files failed to generate download URLs:', errors);
      }
      
      if (validUrls.length === 0) {
        alert('No valid download URLs were generated. Please try again.');
        return;
      }
      
      setDownloadState(prev => ({ ...prev, stage: 'downloading', current: 0, total: validUrls.length }));
      if (onDownloadProgress) onDownloadProgress(0, validUrls.length, 'downloading');
      
      // Fetch blobs using presigned URLs with controlled concurrency
      // Default concurrency of 6 respects browser connection limits while maximizing parallelization
      const files = await fetchFilesFromUrls(validUrls, {
        concurrency: 6, // Optimal for most browsers (6-10 connections per domain)
        onProgress: (current, total) => {
          setDownloadState(prev => ({ ...prev, current, total }));
          if (onDownloadProgress) onDownloadProgress(current, total, 'downloading');
        }
      });

      if (files.length === 0) {
        alert('Failed to download files. Please try again.');
        return;
      }

      // Mobile-only: use Web Share API when available and supported for files
      const isMobile = isMobilePlatform();
      const canNativeShareFiles = typeof navigator !== 'undefined' &&
        typeof navigator.canShare === 'function' &&
        typeof navigator.share === 'function';

      // Check total file size before attempting Web Share API
      // Web Share API has limits: Chrome/Edge ~50MB total, mobile browsers often less
      const WEB_SHARE_MAX_SIZE = 40 * 1024 * 1024; // 40MB
      const totalSize = files.reduce((sum, { blob }) => sum + (blob.size || 0), 0);
      const isSizeTooLarge = totalSize > WEB_SHARE_MAX_SIZE;

      if (isMobile && canNativeShareFiles && !isSizeTooLarge) {
        try {
          const shareableFiles = await Promise.all(files.map(async ({ filename, blob, contentType }) => {
            // Ensure filename has proper extension
            let safeFilename = filename;
            if (!safeFilename.includes('.')) {
              // If no extension, try to determine from blob type or content type
              const detectedType = contentType || blob.type || '';
              if (detectedType.startsWith('image/')) {
                safeFilename = `${safeFilename}.jpg`;
              } else if (detectedType.startsWith('video/')) {
                safeFilename = `${safeFilename}.mp4`;
              }
            }
            
            // Get MIME type: prefer content-type from response, then from filename, then blob type
            const mimeType = contentType || getMimeTypeFromFilename(safeFilename) || blob.type || 'application/octet-stream';
            
            return new File([blob], safeFilename, { type: mimeType });
          }));

          if (navigator.canShare({ files: shareableFiles })) {
            try {
              await navigator.share({
                title: files.length === 1 ? 'Wedding Photo' : 'Wedding Photos',
                text: files.length === 1 ? 'Wedding photo' : `Sharing ${files.length} wedding photos`,
                files: shareableFiles
              });
              if (onDownloadComplete) onDownloadComplete();
              if (onDownloadEnd) onDownloadEnd();
              return;
            } catch (err) {
              // Do not fall back to download for user cancellations or permission denials
              const name = err?.name;
              const message = err?.message || '';
              
              // Check if it's a technical limitation (like "Share too large") vs user cancellation
              const isTechnicalError = message.toLowerCase().includes('too large') || 
                                       message.toLowerCase().includes('share too large') ||
                                       message.toLowerCase().includes('exceeded') ||
                                       message.toLowerCase().includes('limit') ||
                                       message.toLowerCase().includes('size');
              
              // Only exit for true user cancellations, not technical limitations
              if (name === 'AbortError' || name === 'SecurityError') {
                if (!isTechnicalError) {
                  return; // exit without triggering download
                }
              }
              
              // For NotAllowedError, check if it's a technical issue or user denial
              if (name === 'NotAllowedError') {
                if (!isTechnicalError) {
                  return; // exit without triggering download
                }
                // Continue to download fallback below for technical errors
              }
            }
          }
        } catch (error) {
          console.warn('Error preparing files for Web Share API:', error);
          // Continue to download fallback
        }
      }

      // Trigger downloads
      if (files.length === 1) {
        const { filename, blob } = files[0];
        try {
          saveAs(blob, filename);
        } catch (error) {
          console.error('Error triggering single file download:', error);
          throw error;
        }
      } else {
        setDownloadState(prev => ({ ...prev, stage: 'packaging' }));
        if (onDownloadProgress) onDownloadProgress(files.length, files.length, 'packaging');
        
        try {
          const zip = new JSZip();
          files.forEach(({ filename, blob }) => {
            const safeName = filename || `file-${Date.now()}`;
            zip.file(safeName, blob);
          });
          const zipBlob = await zip.generateAsync({ type: 'blob' });
          const archiveName = `wedding-photos-${new Date().toISOString().slice(0,10)}.zip`;
          saveAs(zipBlob, archiveName);
        } catch (error) {
          console.error('Error creating or downloading ZIP:', error);
          throw error;
        }
      }

      setDownloadState(prev => ({ ...prev, stage: 'complete' }));
      if (onDownloadComplete) onDownloadComplete();
    } catch (error) {
      console.error('Download error:', error);
      alert('Failed to download files. Please try again.');
    } finally {
      setDownloadState({ isDownloading: false, current: 0, total: 0, stage: 'idle' });
      if (onDownloadEnd) onDownloadEnd();
    }
  }, [selectedPhotos, onDownloadComplete, onDownloadStart, onDownloadEnd, onDownloadProgress, downloadUrlsMutation]);

  return { downloadSelectedPhotos, downloadState };
};
